import React, { useRef } from 'react';
import { Box, HStack, Input, Textarea, VStack } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { LuX } from 'react-icons/lu';
import ComposerTaskScheduleControls from './ComposerTaskScheduleControls.tsx';
import { TaskComposerController } from './useTaskComposer.ts';
import { DURATION_FORM_REVEAL_OPEN_MS, EASE_OUT } from '@/components/ui/motion.ts';
import { COMPOSER_ACTION_ROW_CONTAINER_CSS, COMPOSER_CHILD_REMOVE_MS } from './composerConstants.ts';

interface ComposerChildTasksProps {
	task: TaskComposerController;
}

/**
 * One-level child-task editor used by the maintenance design trial. Every child owns its
 * own due date and recurrence; nesting another level is deliberately unsupported.
 */
const ComposerChildTasks: React.FC<ComposerChildTasksProps> = ({ task }) => {
	const childCollapseElementsRef = useRef(new Map<string, HTMLDivElement>());

	const removeChild = (id: string) => {
		const element = childCollapseElementsRef.current.get(id);
		if (element) {
			// Establish a concrete start height before React applies the 0px exit target. Unlike a
			// grid-track animation, this leaves the complex form at a fixed intrinsic layout while
			// only the selected row's outer height collapses.
			element.style.height = `${element.getBoundingClientRect().height}px`;
			void element.offsetHeight;
		}
		task.removeChild(id);
	};

	return (
		<VStack align="stretch" gap={0} pt={1}>
			{task.children.map((child, index) => {
				const isRemoving = task.removingChildIds.includes(child.id);

				return (
					<Box
						key={child.id}
						data-composer-child-id={child.id}
						data-composer-child-removing={isRemoving ? 'true' : 'false'}
						display="grid"
						gridTemplateRows="1fr"
						css={{
							transition: `grid-template-rows ${DURATION_FORM_REVEAL_OPEN_MS}ms ${EASE_OUT}`,
							...(index > 0
								? {
										'@starting-style': {
											gridTemplateRows: '0fr',
										},
									}
								: {}),
							'@media (prefers-reduced-motion: reduce)': {
								transition: 'none',
							},
						}}
					>
						<Box
							ref={(element: HTMLDivElement | null) => {
								if (element) childCollapseElementsRef.current.set(child.id, element);
								else childCollapseElementsRef.current.delete(child.id);
							}}
							minH={0}
							style={{ height: isRemoving ? 0 : undefined }}
							overflow="hidden"
							pb={2}
							css={{
								contain: 'layout paint',
								transition: `height ${COMPOSER_CHILD_REMOVE_MS}ms ${EASE_OUT}`,
								'@media (prefers-reduced-motion: reduce)': {
									transition: 'height 1ms linear',
								},
							}}
							onTransitionEnd={(event) => {
								if (
									isRemoving &&
									event.target === event.currentTarget &&
									event.propertyName === 'height'
								) {
									task.completeChildRemoval(child.id);
								}
							}}
						>
							<Box
								borderWidth="1px"
								borderColor="border.subtle"
								borderRadius="2xl"
								bg="surface.subtle"
								px={{ base: 3, md: 4 }}
								py={3}
							>
								<HStack gap={2} align="center">
									<Input
										value={child.title}
										onChange={(event) => task.updateChild(child.id, { title: event.target.value })}
										placeholder="Sub-task title"
										aria-label={`Sub-task ${index + 1} title`}
										variant="flushed"
										border={0}
										textStyle="body.strong"
										h={8}
										px={0}
										disabled={task.isSubmitting}
										_focus={{ boxShadow: 'none' }}
									/>
									<IconButton
										type="button"
										aria-label={`Remove sub-task ${index + 1}`}
										variant="ghost"
										size="xs"
										color="fg.muted"
										_hover={{ bg: 'surface.default' }}
										onClick={() => {
											if (!isRemoving) removeChild(child.id);
										}}
										disabled={task.isSubmitting}
									>
										<LuX />
									</IconButton>
								</HStack>
								<Textarea
									value={child.description}
									onChange={(event) =>
										task.updateChild(child.id, { description: event.target.value })
									}
									placeholder="Add details..."
									aria-label={`Sub-task ${index + 1} details`}
									variant="flushed"
									border={0}
									resize="none"
									rows={1}
									minH={8}
									w="full"
									px={0}
									py={1}
									fontSize="sm"
									disabled={task.isSubmitting}
									_focus={{ boxShadow: 'none' }}
								/>
								<Box mt={2} css={COMPOSER_ACTION_ROW_CONTAINER_CSS}>
									<ComposerTaskScheduleControls task={child} title={child.title} />
								</Box>
							</Box>
						</Box>
					</Box>
				);
			})}
		</VStack>
	);
};

export default ComposerChildTasks;
